import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import {
  BED_TYPE_LABELS,
  bookableQueryKey,
  createBooking,
  fetchBookable,
  initializeBookingPayment,
} from '../api/booking'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import { Button } from '../components/Button'
import { CheckboxField, Field } from '../components/Field'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { StepIndicator } from '../components/StepIndicator'
import { ApiError } from '../lib/api'
import { formatPesewas } from '../lib/money'

/**
 * Bed-booking flow (M10-T08) at `/book/:slug`. The patient picks a bed **type**
 * (ADR-0019: no specific bed; the clinical team assigns it at admission),
 * verifies their phone (M10-T02), and consents that the final bed/room may
 * change on clinical grounds (ADR-0020). "Secure your place" then creates the
 * booking (M10-T04) and starts the deposit on Paystack's hosted page
 * (M10-T05b), redirecting the browser there — the booking is confirmed by the
 * webhook, not the redirect (the patient may not return).
 *
 * No Phase-2 booking wireframe exists in `designs/` yet — this reuses Phase-1
 * atoms/tokens to avoid drift; a wireframe should be added before final polish.
 */
export function BookBed(): ReactNode {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const query = useQuery({
    queryKey: bookableQueryKey(slug),
    queryFn: () => fetchBookable(slug),
  })

  // Deep link from the bed-type picker (M12-T02): `/book/:slug?type=icu`
  // pre-selects that type so the price/deposit shows immediately. An unknown
  // or missing `type` falls back to the type-choice step (null).
  const typeParam = searchParams.get('type')
  const initialType =
    typeParam !== null && typeParam in BED_TYPE_LABELS ? typeParam : null
  const [bedType, setBedType] = useState<string | null>(initialType)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [session, setSession] = useState<PatientSession | null>(null)
  const [consent, setConsent] = useState(false)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: (s) => setSession(s),
  })

  // Secure the place, then start the deposit on Paystack and redirect there. The
  // booking only becomes `secured` when the webhook confirms — not on this
  // redirect — so we leave the page knowing the server is the source of truth.
  const payMutation = useMutation({
    mutationFn: async () => {
      if (session === null || bedType === null) throw new Error('not ready')
      const booking = await createBooking(session.token, slug, bedType)
      const pay = await initializeBookingPayment(
        session.token,
        booking.reference,
      )
      return pay.authorization_url
    },
    onSuccess: (url) => window.location.assign(url),
  })

  const ready = bedType !== null && session !== null && consent
  const leaving = payMutation.isPending || payMutation.isSuccess

  return (
    <PageShell measure={594} as="main" className="font-sans text-ink-1">
      <header
        className={`flex items-center gap-2 border-b border-line-1 px-4 py-3 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded-full p-1 text-ink-3 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={20} />
        </button>
        <h1 className="text-base font-bold tracking-tight">Book a bed</h1>
      </header>
      {/* One band for the body; nothing nested restates the measure and no
          padding moves, so mobile is unchanged by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {query.isPending ? (
          <p className="px-4 py-8 text-sm text-ink-3" role="status">
            Loading availability…
          </p>
        ) : query.isError ? (
          <div className="px-4 py-8">
            <p role="alert" className="text-sm font-medium text-bed-bad">
              {query.error instanceof ApiError &&
              query.error.code === 'not_found'
                ? 'Hospital not found.'
                : "Couldn't load availability."}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-3 rounded-full border border-line-2 bg-bg-app px-4 py-1.5 text-[13px] font-medium"
            >
              Back to search
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-6 px-4 py-5">
            {/* M17-T09 — THREE steps, because the product has three. The frames
              variously draw 4, 2, none and 3; that disagreement is itself the
              evidence that they are not the authority on flow (ADR-0038).
              The current step is derived from what the patient has actually
              done, so it never claims progress they have not made. */}
            <StepIndicator step={session ? 3 : bedType ? 2 : 1} total={3} />

            {query.data.bed_rate_from_pesewas != null && (
              <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-bg-soft px-3 py-1 text-[13px] text-ink-3 ring-1 ring-inset ring-line-1">
                <Icon name="bed" size={13} className="text-ink-3" />
                Beds from{' '}
                <span className="font-semibold text-ink-1">
                  {formatPesewas(query.data.bed_rate_from_pesewas)}
                </span>{' '}
                / night
              </p>
            )}

            {/* 1 — choose a bed type */}
            <section aria-labelledby="type-h">
              <StepHeading id="type-h" n={1}>
                Choose a bed type
              </StepHeading>
              <div className="mt-2 grid grid-cols-1 gap-1.5">
                {query.data.types.map((t) => {
                  const full = t.bookable <= 0
                  const selected = bedType === t.bed_type
                  return (
                    <button
                      key={t.bed_type}
                      type="button"
                      disabled={full}
                      aria-pressed={selected}
                      onClick={() => setBedType(t.bed_type)}
                      className={`flex items-center justify-between rounded-r3 border px-3.5 py-3 text-left text-sm transition-[box-shadow,transform,border-color] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                        selected
                          ? 'border-accent bg-accent-bg text-accent-ink shadow-card ring-1 ring-inset ring-accent/30'
                          : 'border-line-2 bg-bg-app text-ink-1 shadow-xs'
                      } ${
                        full
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-card'
                      }`}
                    >
                      <span className="flex items-center gap-2 font-medium">
                        <Icon
                          name="bed"
                          size={16}
                          className={selected ? 'text-accent' : 'text-ink-3'}
                        />
                        {BED_TYPE_LABELS[t.bed_type] ?? t.bed_type}
                      </span>
                      <span
                        className={`text-[12px] font-medium ${full ? 'text-bed-bad' : selected ? 'text-accent-ink' : 'text-bed-good'}`}
                      >
                        {full ? 'Full' : `${t.bookable} available`}
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>

            {/* 2 — verify phone */}
            <section aria-labelledby="phone-h">
              <StepHeading id="phone-h" n={2}>
                Verify your phone
              </StepHeading>
              {session ? (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
                  <Icon name="check" size={14} /> Verified · {phone.trim()}
                </p>
              ) : (
                <div className="mt-2 flex flex-col gap-2">
                  {/* M17-T09 — the shared Field primitive. The visible label
                    replaces the old aria-label and produces the same
                    accessible name, so the existing tests still find it by
                    'Phone number'. The resend affordance is unchanged
                    (ADR-0021): same button, same copy, same conditions. */}
                  <Field
                    label="Phone number"
                    type="tel"
                    placeholder="Phone, e.g. +233…"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    {...(requestMutation.isError
                      ? { error: errMsg(requestMutation.error) }
                      : {})}
                    trailing={
                      <button
                        type="button"
                        disabled={
                          phone.trim().length < 8 || requestMutation.isPending
                        }
                        onClick={() => requestMutation.mutate()}
                        className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      >
                        {requestMutation.isPending
                          ? 'Sending…'
                          : codeSent
                            ? 'Resend'
                            : 'Send code'}
                      </button>
                    }
                  />
                  {codeSent && (
                    <Field
                      label="Verification code"
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      {...(verifyMutation.isError
                        ? { error: errMsg(verifyMutation.error) }
                        : {})}
                      trailing={
                        <button
                          type="button"
                          disabled={
                            code.trim().length < 4 || verifyMutation.isPending
                          }
                          onClick={() => verifyMutation.mutate()}
                          className="shrink-0 rounded-r3 bg-accent px-3 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
                        </button>
                      }
                    />
                  )}
                </div>
              )}
            </section>

            {/* 3 — consent + (placeholder) pay */}
            <section aria-labelledby="confirm-h">
              <StepHeading id="confirm-h" n={3}>
                Confirm
              </StepHeading>
              <CheckboxField
                className="mt-2"
                checked={consent}
                onChange={setConsent}
                label={
                  <>
                    I understand my deposit secures a <strong>place</strong>,
                    not a specific bed, and that my final bed or room may change
                    on clinical grounds.
                  </>
                }
              />

              {/* M17-T03 — migrated to the shared Button primitive. Same handler,
                same copy, same disabled semantics; the geometry now comes from
                the ported button (radius 16, padding 24/10) rather than from
                this call site. */}
              <Button
                full
                icon="lock"
                disabled={!ready || leaving}
                aria-disabled={!ready || leaving}
                loading={leaving}
                onClick={() => payMutation.mutate()}
                className="mt-4 shadow-sm"
              >
                {leaving ? 'Starting secure checkout…' : 'Secure your place'}
              </Button>
              {payMutation.isError ? (
                <p
                  role="alert"
                  className="mt-1.5 text-center text-[12px] text-bed-bad"
                >
                  {payErrMsg(payMutation.error)}
                </p>
              ) : (
                <p className="mt-1.5 text-center text-[12px] text-ink-3">
                  {ready
                    ? 'You’ll pay your deposit securely on Paystack (card or Mobile Money).'
                    : 'Choose a bed type, verify your phone, and consent to continue.'}
                </p>
              )}
            </section>
          </div>
        )}
      </PageBand>
    </PageShell>
  )
}

/** Numbered section heading for the booking steps. */
function StepHeading({
  id,
  n,
  children,
}: {
  id: string
  n: number
  children: ReactNode
}): ReactNode {
  return (
    <h2
      id={id}
      className="flex items-center gap-2 text-sm font-semibold text-ink-1"
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-full bg-accent-bg text-[12px] font-bold text-accent-ink ring-1 ring-inset ring-accent/20"
        aria-hidden="true"
      >
        {n}
      </span>
      {children}
    </h2>
  )
}

function errMsg(err: unknown): string {
  return err instanceof ApiError
    ? err.message
    : 'Something went wrong. Try again.'
}

/** Patient-facing copy for the create-booking + initialize-payment failures. */
function payErrMsg(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'no_capacity':
        return 'That bed type just filled up — please pick another.'
      case 'payment_unavailable':
        return 'Payment is temporarily unavailable. Please try again in a moment.'
      case 'hold_expired':
        return 'Your hold expired — please start again.'
      default:
        return err.message
    }
  }
  return 'Could not start payment. Please try again.'
}
